import scrollMonitor from 'scrollmonitor'
import merge from 'lodash.merge'

const DEFAULT_SIZES = {
  small: {
    width: 734
  },
  medium: {
    width: 862
  },
  large: {
    width: 1072
  }
}

const DEFAULT_LOADING_OFFSET = {
  body: {
    top: 100,
    bottom: 100
  },
  container: {
    top: 100,
    bottom: 350
  },
}

export default {
  props: {
    alt: {
      type: String,
      default: null
    },
    scrollContainerId: {
      type: String,
      default: null
    },
    autoQuality: {
      type: String,
      default: 'auto',
      validator: (val) => ['auto', 'low', 'eco', 'good', 'best'].includes(val)
    },
    manualQuality: {
      type: Number,
      default: null,
      validator: (val) => val > 0 && val <= 100
    },
    sharpen: {
      type: Number,
      default: null,
      validator: (val) => val > 0 && val <= 2000
    },
    // Set to true to utilize the c_fit transform insted of c_lfill transform in cloudinary
    // For reference see: https://cloudinary.com/documentation/image_transformation_reference#crop_parameter
    // Think background-size: contain; whereas the default would be background-size: cover
    fitDimensions: {
      type: Boolean,
      default: false
    },
    image: {
      type: Object,
      default: null
    },
    usePicture: {
      type: Boolean,
      default: true
    },
    sizes: {
      type: Object,
      default () {
        return DEFAULT_SIZES
      }
    },
    namedTransformations: {
      type: Object,
      default: () => {}
    },
    manualDpr: {
      type: Number || null,
      default: null,
      validator: (val) => val >= 1 && val <= 3
    },
  },
  data () {
    return {
      failed: false,
      loaded: false,
      watcher: null
    }
  },
  computed: {
    description () {
      return this.image ? this.image.altText || this.alt : this.alt
    },
    sizeSettings () {
      return merge({}, DEFAULT_SIZES, this.sizes)
    },
    backgroundImageStyle () {
      let ar = (this.autoAspectRatio !== false || this.usePicture) ? `padding-bottom:${this.getImageRatio()}%;` : ''
      let backgroundImage = this.loaded ? `background-image:url("${this.currentImageUrl}");` : ''

      return `${backgroundImage}${ar}`
    },
    currentImageUrl () {
      return this.getCloudinaryUrl(this.image[this.currentSize], this.sizeSettings[this.currentSize])
    },

    emptyImageObject () {
      return !this.image
    },
    currentSize () {
      switch (this.$root.currentSize) {
        case 'xl':
        case 'lg':
          return 'large'
        case 'md':
          return 'medium'
      }

      return 'small'
    },
    qualityParam () {
      if (this.manualQuality) {
        return `q_${Math.round(this.manualQuality)}`
      } else if (this.autoQuality === 'auto') {
        return 'q_auto'
      } else {
        return `q_auto:${this.autoQuality}`
      }
    },
    sharpenParam () {
      if (this.sharpen) {
        return `/e_sharpen:${Math.round(this.sharpen)}`
      } else {
        return ''
      }
    },
    dprParam () {
      if (this.manualDpr) {
        return `dpr_${this.manualDpr}`
      } else if (SSR_MODE) {
        return "dpr_1"
      }

      return `dpr_${(
        !devicePixelRatio ? 1 : //Doesn't have that feature... return 1
        devicePixelRatio < 1 ? 1 : //DPR below 1, return 1
        devicePixelRatio > 2 ? 2 :  //DPR above 2, only return 2. The current max we are allowing
        Math.round(devicePixelRatio) //Otherwise return the value rounded  (e.g. 1.1 => 1, 1.5 => 2)
      ).toFixed(1)}` //We want the value as a string with one decimal. 1 => "1.0"
    },
    hasImage () {
      // DEFAULT, SHOULD OVERRIDE IN IMPLEMENTATION
      return true
    }
  },
  mounted () {
    this.createScrollmonitor()
  },
  methods: {
    createScrollmonitor () {
      if (this.hasImage) {
        const { body: bodyOffset, container: containerOffset } = DEFAULT_LOADING_OFFSET

        if(!this.scrollContainerId) {
          this.watcher = scrollMonitor.create(this.$el, bodyOffset)
        } else {
          const containerElement = document.getElementById(this.scrollContainerId)
          if(!containerElement) {
            console.warn('Could not find scroll container with  id: ', this.scrollContainerId)
            console.log('creating scroll monitor on body instead')
            this.watcher = scrollMonitor.create(this.$el, bodyOffset)
          } else {
            const containerMonitor = scrollMonitor.createContainer(containerElement)
            this.watcher = containerMonitor.create(this.$el, containerOffset) 
          }
        }
  
        this.watcher.enterViewport(() => {
          //setTimeout fixes some initial "choppyness", especially when fetched from cache
          setTimeout(this.loadImage, 0)
        })
      } else {
        this.loaded = true
      }
    },
    loadImage () {
      const img = this.$refs.lazyLoadImage
      if (img) {
        img.src = this.currentImageUrl
      }
    },
    lazyLoadComplete () {
      this.loaded = true
      this.watcher && this.watcher.destroy()
    },
    lazyLoadFailed () {
      this.failed = true
      this.watcher && this.watcher.destroy()
    },
    getDimensions (img) {
      const {
        left = 0,
        right = 0,
        top = 0,
        bottom = 0,
        width = 0,
        height = 0,
        proportions: { x, y } = {
          proportions: {
          x: 0, y: 0
          }
        }
      } = img || {}

      return {
        w: Math.round((1 - (left + right)) * width),
        h: Math.round((1 - (bottom + top)) * height),
        x: Math.round(x || left * width),
        y: Math.round(y || top * height)
      }
    },
    getDynamicCloudinaryUrl () {
      // Due to the nature of reactive properties and methods in Vue, this function gets called
      // every time this.currentSize changes. This ought to be sufficient for most cases,
      // otherwise this should be updated more explicitly with a throttled resizeobserver

      let dimension = {
        width: this.$el.offsetWidth,
        height: this.$el.offsetHeight
      }

      return this.getCloudinaryUrl(this.image[this.currentSize], dimension)
    },
    getCloudinaryUrl (img, dimension) {
      if (!img) {
        return
      }
      const
        { w,h,x,y } = this.getDimensions(img),
        filename = encodeURIComponent(img.filename.trim())
      let scaleParams = ''

      if (dimension.height) {
        scaleParams = `/w_${dimension.width},h_${dimension.height},c_lfill`
      } else if (dimension.width < w) {
        scaleParams = `/w_${dimension.width},h_${Math.round(dimension.width*h/w)},c_scale`
      }
      let catalogType = img.provider || img.catalog
      return `${this.$root.envConfig.cloudinaryBaseUrl}/image/upload/w_${w},h_${h},x_${x},y_${y},c_crop${scaleParams}${this.sharpenParam}/f_auto,${this.qualityParam},${this.dprParam}/cumulus/${catalogType}/${img.id}/${filename}`
    }
  },
}
